// src/components/UserCard.tsx
import React from 'react';
import { User } from 'lucide-react';
import { hasAdminAccess } from '../utils/roleUtils';

// Define the props interface
interface UserCardProps {
  user: {
    _id: string;
    firstName?: string;
    lastName?: string;
    profile?: {
      filePath?: string;
      fileName?: string;
    };
    roleKey?: string;
    designation?: string;
  };
  size?: 'sm' | 'md' | 'lg';
  showRole?: boolean;
  onClick?: () => void;
  isSelected?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

// Base URL for file storage (update this with your actual value)
const BUCKET_URL = 'https://all-user-version-4.s3.us-east-1.amazonaws.com/';

const UserCard: React.FC<UserCardProps> = ({
  user,
  size = 'md',
  showRole = false,
  onClick,
  isSelected = false,
  className = '',
  style = {}
}) => {
  // Handle missing user data gracefully
  if (!user || !user._id) {
    return (
      <div 
        className={`d-flex align-items-center ${className} ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
        style={style}
      >
        <div 
          className="d-flex align-items-center justify-content-center rounded-circle bg-gray-200 text-gray-500"
          style={{ 
            width: size === 'sm' ? 28 : size === 'md' ? 36 : 48,
            height: size === 'sm' ? 28 : size === 'md' ? 36 : 48,
            marginRight: 8
          }}
        >
          <User size={size === 'sm' ? 14 : size === 'md' ? 18 : 24} />
        </div>
        <div className="text-gray-500">Unassigned</div>
      </div>
    );
  }

  // Format the name correctly
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ') || 'Unknown';
  
  // Get profile picture URL
  const profileFilePath = user.profile?.filePath;
  const hasProfilePic = !!profileFilePath;
  const profileUrl = hasProfilePic 
    ? `${BUCKET_URL}${profileFilePath}`
    : undefined;

  // Determine background color for selection state
  const bgColor = isSelected ? '#E6F4FF' : 'transparent';
  const borderColor = isSelected ? '#1890FF' : 'transparent';

  return (
    <div 
      className={`d-flex align-items-center p-2 rounded ${className} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      style={{ 
        backgroundColor: bgColor, 
        border: `1px solid ${borderColor}`,
        transition: 'background-color 0.2s',
        ...style
      }}
    >
      {/* Profile Picture or Initials */}
      {profileUrl ? (
        <div 
          className="rounded-circle bg-cover bg-center"
          style={{ 
            width: size === 'sm' ? 28 : size === 'md' ? 36 : 48,
            height: size === 'sm' ? 28 : size === 'md' ? 36 : 48,
            backgroundImage: `url(${profileUrl})`,
            marginRight: 8
          }}
        />
      ) : (
        <div 
          className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
          style={{ 
            width: size === 'sm' ? 28 : size === 'md' ? 36 : 48,
            height: size === 'sm' ? 28 : size === 'md' ? 36 : 48,
            marginRight: 8
          }}
        >
          {fullName.substring(0, 1).toUpperCase()}
        </div>
      )}

      {/* User Details */}
      <div className="d-flex flex-column">
        <div className="font-weight-medium">{fullName}</div>
        
        {showRole && (user.roleKey || user.designation) && (
          <div className="text-muted small">
            {hasAdminAccess(user.roleKey)
              ? 'Admin'
              : user.roleKey?.replace(/_/g, ' ') || user.designation || ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(UserCard);